import { type DocumentData, limit, orderBy, query, where } from 'firebase/firestore'

import { callable } from '@/lib/firebase/callable'
import { type Subscription, subscribeCollection } from '@/lib/firebase/subscribe'
import {
  type SetupScope,
  asNumber,
  asOneOf,
  asRecord,
  asString,
  asStringOrNull,
  setupCollection,
  toSetupRun,
  toStageEntryBase,
} from '@/repositories/setup-docs'
import {
  ARTICLE_RUN_ERROR_CODES,
  ARTICLE_RUN_STAGES,
  GAIN_KINDS,
  type ArticleArgument,
  type ArticleQuestion,
  type ArticleRun,
  type ArticleRunStageEntry,
  type Gain,
} from '@/types/article-run'
import { OPEN_RUN_STATUSES } from '@/types/setup-run'

function rows<T>(value: unknown, toRow: (row: DocumentData) => T | undefined): T[] {
  return (Array.isArray(value) ? value : []).flatMap((entry: unknown) => {
    const row = toRow(asRecord(entry))
    return row === undefined ? [] : [row]
  })
}

function toGain(data: DocumentData): Gain | undefined {
  if (typeof data.id !== 'string') return undefined
  return {
    id: data.id,
    itemId: asStringOrNull(data.itemId),
    kind: asOneOf(data.kind, GAIN_KINDS, 'gap'),
    text: asString(data.text),
    quotes: rows(data.quotes, (quote) => ({
      source: asString(quote.source),
      quote: asString(quote.quote),
    })),
    why: asString(data.why),
  }
}

function toArgument(value: unknown): ArticleArgument | null {
  if (value === null || value === undefined) return null
  const data = asRecord(value)
  const belief = data.belief ? asRecord(data.belief) : null
  const item = data.item ? asRecord(data.item) : null
  return {
    source: data.source === 'plan' ? 'plan' : 'stage',
    belief: belief
      ? {
          slug: asString(belief.slug),
          claim: asString(belief.claim),
          objection: asString(belief.objection),
        }
      : null,
    item: item ? { id: asString(item.id), item: asString(item.item) } : null,
    proof: rows(data.proof, (proof) => ({
      id: asString(proof.id),
      text: asString(proof.text),
      by: asString(proof.by),
      url: asStringOrNull(proof.url),
    })),
    why: asStringOrNull(data.why),
  }
}

function toStageEntry(entry: DocumentData): ArticleRunStageEntry | undefined {
  const id = entry.id
  if (!(ARTICLE_RUN_STAGES as readonly string[]).includes(id)) return undefined
  return { ...toStageEntryBase(entry), id, counts: asRecord(entry.counts) } as ArticleRunStageEntry
}

function toQuestion(data: DocumentData): ArticleQuestion | undefined {
  if (typeof data.id !== 'string') return undefined
  return {
    id: data.id,
    itemId: asStringOrNull(data.itemId),
    item: asString(data.item),
    section: asStringOrNull(data.section),
    ask: asString(data.ask),
    why: asString(data.why),
    hint: asString(data.hint),
    without: asString(data.without),
    answer: asStringOrNull(data.answer),
    skipped: data.skipped === true,
    at: asStringOrNull(data.at),
  }
}

/** The article run as the client reads it; a stage the client does not know is dropped. */
export function toArticleRun(id: string, data: DocumentData): ArticleRun {
  const { errorCode, ...run } = toSetupRun(id, data, ARTICLE_RUN_STAGES, toStageEntry)
  const market = asRecord(data.market)
  return {
    ...run,
    ...(errorCode === undefined
      ? {}
      : { errorCode: asOneOf(errorCode, ARTICLE_RUN_ERROR_CODES, 'article_run_failed') }),
    pageId: asString(data.pageId),
    topicId: asString(data.topicId),
    postId: asStringOrNull(data.postId),
    startedByUserId: asStringOrNull(data.startedByUserId),
    market: {
      locationCode: asNumber(market.locationCode),
      languageCode: asString(market.languageCode),
    },
    questions: rows(data.questions, toQuestion),
    gains: Array.isArray(data.gains) ? rows(data.gains, toGain) : null,
    argument: toArgument(data.argument),
  }
}

/** Live view of one page's most recent article run; `null` before its first. */
export function subscribeLatestArticleRun(
  scope: SetupScope,
  pageId: string,
): Subscription<ArticleRun | null> {
  return subscribeCollection(
    query(
      setupCollection(scope, 'articleRuns'),
      where('pageId', '==', pageId),
      orderBy('createdAt', 'desc'),
      limit(1),
    ),
    (snapshot) => {
      const [latest] = snapshot.docs
      return latest ? toArticleRun(latest.id, latest.data()) : null
    },
  )
}

/** Live view of the brand's article runs that have not ended, on any page. */
export function subscribeOpenArticleRuns(scope: SetupScope): Subscription<ArticleRun[]> {
  return subscribeCollection(
    query(setupCollection(scope, 'articleRuns'), where('status', 'in', OPEN_RUN_STATUSES)),
    (snapshot) => snapshot.docs.map((doc) => toArticleRun(doc.id, doc.data())),
  )
}

export interface WritePageResult {
  readonly runId: string
}

const writePageCallable = callable<{ pageId: string }, WritePageResult>('writePage')

/** Opens the article run of one planned page; resolves the run id. */
export async function writePage(pageId: string): Promise<WritePageResult> {
  return writePageCallable({ pageId })
}

/** The company's reply to one question: an answer, or a skip. */
export type QuestionReply = { readonly answer: string } | { readonly skip: true }

const answerArticleQuestionCallable = callable<
  { runId: string; questionId: string; answer?: string; skip?: true },
  { open: number }
>('answerArticleQuestion')

/** Records the company's reply to one question of a waiting run; resolves how many questions stay open. */
export async function answerArticleQuestion(
  runId: string,
  questionId: string,
  reply: QuestionReply,
): Promise<{ open: number }> {
  return answerArticleQuestionCallable({ runId, questionId, ...reply })
}
